import { useTranslation } from 'react-i18next';
import { Sparkles, Check, AlertTriangle, Clock } from 'lucide-react';
import {
  CATEGORY_CONFIG, displayConfidencePercent, type TalentCategory,
} from './talentMapShared';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface AgentReadingRecord {
  leaderProposedCategory?: string | null;
  agentRecommendedCategory?: string | null;
  confidenceLevel?: 'high' | 'medium' | 'low' | null;
  confidencePercent?: number | null;
  evidenceEffective?: string[];
  evidenceAffective?: string[];
  evidencePerspective?: string[];
  strengths?: string[];
  developmentOpportunities?: string[];
  missingEvidence?: string[];
  calibrationAlerts?: string[];
  processStatus?: string;
  hrbpModifiedBy?: string | null;
  hrbpModifiedAt?: string | null;
  hrbpJustification?: string | null;
  hrbpNewCategory?: string | null;
}

function CategoryChip({ category, highlighted }: { category?: string | null; highlighted?: boolean }) {
  const { t } = useTranslation();
  const cfg = category ? CATEGORY_CONFIG[category as TalentCategory] : null;
  if (!cfg) return <span className="text-sm text-muted-foreground">—</span>;
  return (
    <span className={cn(
      'inline-flex items-center rounded-md px-3 py-1.5 text-sm font-semibold',
      highlighted ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300' : 'bg-muted text-foreground',
    )}>
      {t(cfg.labelKey)}
    </span>
  );
}

/** Right panel of the "Evaluación individual" tab — "✦ Lectura del agente"
 *  per the spec mockup. Renders live from the record, which useProcessTalentMessage
 *  keeps fresh by invalidating getEvaluation after every message. */
export function AgentReadingPanel({ record }: { record: AgentReadingRecord | null }) {
  const { t } = useTranslation();
  const confidence = displayConfidencePercent(record);
  const sectionTitle = 'text-[11px] font-semibold tracking-[0.15em] uppercase text-muted-foreground';

  return (
    <div className="space-y-5">
      <h3 className="font-semibold flex items-center gap-2">
        <Sparkles className="h-4 w-4" />
        {t('performance.talent_map.evaluation_tab.agent_reading')}
      </h3>

      <div className="space-y-1.5">
        <p className={sectionTitle}>{t('performance.talent_map.evaluation_tab.leader_proposal')}</p>
        <CategoryChip category={record?.leaderProposedCategory} />
      </div>

      <div className="space-y-1.5">
        <p className={sectionTitle}>{t('performance.talent_map.evaluation_tab.agent_recommendation')}</p>
        <CategoryChip category={record?.agentRecommendedCategory} highlighted />
      </div>

      <div className="space-y-1.5">
        <p className={sectionTitle}>{t('performance.talent_map.evaluation_tab.confidence')}</p>
        {confidence != null ? (
          <>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-foreground transition-all" style={{ width: `${confidence}%` }} />
              </div>
              <span className="text-sm font-semibold tabular-nums shrink-0">{confidence}%</span>
            </div>
            <p className="text-xs text-muted-foreground">{t('performance.talent_map.evaluation_tab.confidence_caption')}</p>
          </>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </div>

      {((record?.evidenceEffective?.length ?? 0) +
        (record?.evidenceAffective?.length ?? 0) +
        (record?.evidencePerspective?.length ?? 0)) > 0 && (
        <div className="space-y-2.5">
          <p className={sectionTitle}>
            {t('performance.talent_map.evaluation_tab.evidence_by_dimension', { defaultValue: 'Evidencia por dimensión' })}
          </p>
          {([
            ['Effective', record?.evidenceEffective],
            ['Affective', record?.evidenceAffective],
            ['Perspective', record?.evidencePerspective],
          ] as const).map(([dim, items]) =>
            (items?.length ?? 0) > 0 ? (
              <div key={dim} className="space-y-1">
                <p className="text-[11px] font-semibold text-foreground/70">{dim}</p>
                <ul className="space-y-1">
                  {items!.map((ev, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                      <span className="text-muted-foreground shrink-0 leading-5">·</span>
                      <span>{ev}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null
          )}
        </div>
      )}

      {(record?.strengths?.length ?? 0) > 0 && (
        <div className="space-y-1.5">
          <p className={sectionTitle}>{t('performance.talent_map.evaluation_tab.observed_strengths')}</p>
          <ul className="space-y-1">
            {record!.strengths!.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(record?.developmentOpportunities?.length ?? 0) > 0 && (
        <div className="space-y-1.5">
          <p className={sectionTitle}>{t('performance.talent_map.evaluation_tab.dev_opportunities')}</p>
          <ul className="space-y-1">
            {record!.developmentOpportunities!.map((o, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                <span className="text-muted-foreground shrink-0 leading-5">○</span>
                <span>{o}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {record?.processStatus === 'pending_evidence' && (record?.missingEvidence?.length ?? 0) > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-amber-700 text-xs font-semibold">
            <Clock className="h-3.5 w-3.5" />
            {t('performance.talent_map.pending_evidence_panel')}
          </div>
          {record!.missingEvidence!.map((item, i) => (
            <p key={i} className="text-xs text-amber-700">· {item}</p>
          ))}
        </div>
      )}

      {(record?.calibrationAlerts?.length ?? 0) > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-amber-700 text-xs font-semibold">
            <AlertTriangle className="h-3.5 w-3.5" />
            {t('performance.talent_map.calibration_alerts')}
          </div>
          {record!.calibrationAlerts!.map((alert, i) => (
            <p key={i} className="text-xs text-amber-700">{alert}</p>
          ))}
        </div>
      )}

      {record?.hrbpModifiedBy && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 p-3 space-y-1">
          <p className="text-xs font-semibold text-blue-700">{t('performance.talent_map.hrbp_modified_label')}</p>
          {record.hrbpNewCategory && (
            <p className="text-xs text-blue-700 font-medium">
              {t('performance.talent_map.new_category_label')}: {t(CATEGORY_CONFIG[record.hrbpNewCategory as TalentCategory]?.labelKey ?? '')}
            </p>
          )}
          {record.hrbpJustification && <p className="text-xs text-blue-600">{record.hrbpJustification}</p>}
          <p className="text-xs text-muted-foreground">
            {record.hrbpModifiedBy.split('@')[0]}
            {record.hrbpModifiedAt && <> · {format(new Date(record.hrbpModifiedAt), 'dd/MM/yyyy', { locale: es })}</>}
          </p>
        </div>
      )}
    </div>
  );
}
